import { getTicketByID } from '@/modules/Ticket/services'
import { IPermissionProps } from '@/types/shared'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { ClipboardList } from 'lucide-react'
import TicketMajor from './MajorTicket'
import ConvertInProgress from './ConvertInProgress'
import Inprogress from './Processing'
import CloseTicket from './Closed'
import WorkflowStepper from './WorkflowStepper'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    TICKET_STATUS_COMPLETED,
    TICKET_STATUS_CLOSED,
    TICKET_STATUS_OPENING,
    TICKET_STATUS_WORK_IN_PROGRESS,
    TICKET_STATUS_ESCALATED,
} from '@/modules/Ticket/constants'

const TicketReports: React.FC<IPermissionProps> = () => {
    const { ticketID } = useParams<{ ticketID: string }>()
    const { t } = useTranslation()

    const { data: ticket, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_DETAIL, ticketID],
        queryFn: async () => {
            const result = await getTicketByID({
                ticket_id: Number(ticketID) || 0,
            })
            return result
        },
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const ticketData = ticket?.result
    const currentStatus = ticketData?.ticket_status || 'opening'
    const isServiceTicket = ticketData?.ticket_type_group === 'service'

    return (
        <div className="border-t border-gray-200">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-gray-500" />
                <Typogaphy className="text-sm font-medium text-gray-700">
                    {t('ticket-processing-workflow')}
                </Typogaphy>
            </div>

            {/* Workflow Stepper */}
            <div className="px-4 py-4 bg-gray-50">
                <WorkflowStepper
                    currentStatus={currentStatus}
                    isServiceTicket={isServiceTicket}
                />
            </div>

            {/* Content */}
            <div className="p-4 border-t border-gray-200">
                {/* Major Ticket Banner */}
                {ticketData?.ticket_marjorable && (
                    <div className="mb-4">
                        <TicketMajor ticket={ticketData} />
                    </div>
                )}

                {/* Status-specific content */}
                {currentStatus === TICKET_STATUS_OPENING && (
                    <ConvertInProgress ticket={ticketData} />
                )}

                {/* Escalating flags a ticket for attention; it does not hand it
                    to anyone or take it out of processing, so it keeps the same
                    controls as work-in-progress. Without this branch the section
                    rendered nothing at all and the ticket became unworkable for
                    everyone — assignee, escalator and lead alike — which is not
                    a permission problem and so no role could work around it. */}
                {[
                    TICKET_STATUS_WORK_IN_PROGRESS,
                    TICKET_STATUS_ESCALATED,
                ].includes(currentStatus) && <Inprogress ticket={ticketData} />}

                {[TICKET_STATUS_CLOSED, TICKET_STATUS_COMPLETED].includes(
                    currentStatus
                ) && <CloseTicket ticket={ticketData} />}
            </div>
        </div>
    )
}
export default TicketReports
